import { forwardRef, ButtonHTMLAttributes } from 'react'
import { haptic, type HapticStyle } from '@/lib/haptics'

type ButtonVariant = 'primary' | 'secondary' | 'outline'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  fullWidth?: boolean
  isLoading?: boolean
  hapticStyle?: HapticStyle | false
}

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-[#46C0C6] hover:bg-[#3aa8ad] active:bg-[#339a9f] text-white',
  secondary: 'bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-900',
  outline: 'bg-transparent border-2 border-[#46C0C6] text-[#46C0C6] hover:bg-[#46C0C6] hover:text-white active:bg-[#3aa8ad]',
}

const sizes: Record<ButtonSize, string> = {
  sm: 'py-2 px-4 text-sm min-h-[36px]',
  md: 'py-3 px-6 text-base min-h-[44px]',
  lg: 'py-4 px-8 text-lg min-h-[52px]',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      isLoading = false,
      hapticStyle = 'light',
      className = '',
      children,
      disabled,
      onClick,
      ...props
    },
    ref
  ) => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!disabled && !isLoading && hapticStyle !== false) {
        haptic(hapticStyle)
      }
      onClick?.(e)
    }

    return (
      <button
        ref={ref}
        onClick={handleClick}
        className={`
          ${variants[variant]}
          ${sizes[size]}
          ${fullWidth ? 'w-full' : ''}
          font-medium rounded-full
          transition-all duration-150 ease-out
          transform active:scale-[0.97]
          disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100
          touch-manipulation
          ${className}
        `}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <svg
              className="animate-spin h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Loading...
          </span>
        ) : (
          children
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'
